'use client';

import { useEffect, useRef } from 'react';
import useUser from '@alltrails/context/hooks/useUser';
import { useClientNavigationPathname } from '@alltrails/core/hooks/useClientNavigation';

const BRAZE_KEY = `${process.env.NEXT_PUBLIC_BRAZE_KEY}`;

const useBraze = ({ disableInAppMessages }: { disableInAppMessages?: boolean } = {}) => {
  const user = useUser();
  const userId = user?.id?.toString();
  const pathname = useClientNavigationPathname();

  // Store removeSubscriptionRef so that it can dynamically imported and called in the effect cleanup
  const removeSubscriptionRef = useRef<((id: string) => void) | null>(null);
  const subscriptionIdRef = useRef<string | undefined>(undefined);

  useEffect(() => {
    if (BRAZE_KEY) {
      const configureBraze = async () => {
        const { initialize, openSession, changeUser, subscribeToInAppMessage, showInAppMessage, removeSubscription } =
          await import('./braze-exports');

        removeSubscriptionRef.current = removeSubscription;

        initialize(BRAZE_KEY, {
          baseUrl: 'https://sdk.iad-03.braze.com',
          allowUserSuppliedJavascript: true
        });

        /* 
          Exclude these paths from showing in-app messages:
          - /plans/* and /plus/*
          - /25hikes
          - /nbc-the-americas
          - /2025-summer
        */
        subscriptionIdRef.current = subscribeToInAppMessage(inAppMessage => {
          const excludedIamPath = /\/(plans|plus|25hikes|nbc-the-americas|2025-summer|tmobile|meta)/.test(pathname);
          if (!excludedIamPath && !disableInAppMessages) {
            showInAppMessage(inAppMessage);
          }
        });

        if (userId) {
          changeUser(userId);
        }

        openSession();
      };

      configureBraze();
    }

    return () => {
      // Cleanup previous IAM subscriptions when user or pathname changes.
      if (subscriptionIdRef.current && removeSubscriptionRef.current) {
        removeSubscriptionRef.current(subscriptionIdRef.current);
      }
    };
  }, [pathname, userId, disableInAppMessages]);
};

export default useBraze;
